/**
 * Composable Intervention Block & Recipe Types
 * Luminous Break - Clinician Recipe Builder & Client Runtime
 */

import { BehavioralDomainId } from './target-types';

export type ComposableBlockType = 'somatic' | 'cognitive' | 'friction' | 'support';

export interface SomaticBlockConfig {
  variant: 'box_4_4_4' | 'relaxing_4_7_8' | 'diaphragmatic_mandala';
  cycles: number;
  inhaleSeconds: number;
  holdSeconds: number;
  exhaleSeconds: number;
  holdAfterExhaleSeconds?: number;
}

export interface CognitiveBlockConfig {
  variant: 'cbt_reframing' | 'craving_vs_need' | 'loss_probability' | 'urge_surfing';
  promptQuestion: string;
  distortionTags?: string[];
  minSentenceCount?: number;
}

export interface FrictionBlockConfig {
  variant: 'cooling_off_lock' | 'values_contract' | 'reflection_prompt';
  lockDurationSeconds: number;
  pledgeText?: string;
  coreValues?: string[];
}

export interface SupportBlockConfig {
  variant: 'voice_video_note' | 'crisis_988' | 'sponsor_dialer';
  mediaUrl?: string;
  mediaType?: 'audio' | 'video';
  mediaTitle?: string;
  transcript?: string;
  sponsorPhoneNumber?: string;
  durationSeconds?: number;
  storagePath?: string;
}

export interface ComposableBlock {
  id: string;
  type: ComposableBlockType;
  title: string;
  instructions: string;
  somaticConfig?: SomaticBlockConfig;
  cognitiveConfig?: CognitiveBlockConfig;
  frictionConfig?: FrictionBlockConfig;
  supportConfig?: SupportBlockConfig;
}

export interface InterventionRecipe {
  id: string;
  name: string;
  domainId: BehavioralDomainId;
  isClinicianAssigned: boolean;
  clinicianId?: string;
  clinicianName?: string;
  clientId?: string;
  clientName?: string;
  blocks: ComposableBlock[];
  encryptedPayload?: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Block Factory Functions
// ---------------------------------------------------------------------------

let idCounter = 1;
export const generateBlockId = (prefix: string = 'blk'): string => {
  return `${prefix}_${Date.now().toString(36)}_${(idCounter++).toString(36)}`;
};

export function createDefaultSomaticBlock(
  variant: SomaticBlockConfig['variant'] = 'box_4_4_4',
  overrides?: Partial<SomaticBlockConfig>
): ComposableBlock {
  let cycles = 4;
  let inhaleSeconds = 4;
  let holdSeconds = 4;
  let exhaleSeconds = 4;
  let holdAfterExhaleSeconds = 0;
  let title = 'Box Breathing (4-4-4)';
  let instructions = 'Inhale deeply, hold steady, and exhale smoothly to calm your nervous system.';

  if (variant === 'relaxing_4_7_8') {
    cycles = 3;
    inhaleSeconds = 4;
    holdSeconds = 7;
    exhaleSeconds = 8;
    title = 'Relaxing Breath (4-7-8)';
    instructions = 'Extended exhalation activates parasympathetic relaxation and lowers heart rate.';
  } else if (variant === 'diaphragmatic_mandala') {
    cycles = 3;
    inhaleSeconds = 5;
    holdSeconds = 0;
    exhaleSeconds = 5;
    title = 'Diaphragmatic Mandala Wave';
    instructions = 'Slow belly breathing with rhythmic visual expansion to center awareness.';
  }

  const somaticConfig: SomaticBlockConfig = {
    variant,
    cycles,
    inhaleSeconds,
    holdSeconds,
    exhaleSeconds,
    holdAfterExhaleSeconds,
    ...overrides,
  };

  return {
    id: generateBlockId('som'),
    type: 'somatic',
    title,
    instructions,
    somaticConfig,
  };
}

export function createDefaultCognitiveBlock(
  variant: CognitiveBlockConfig['variant'] = 'cbt_reframing',
  prompt?: string,
  overrides?: Partial<CognitiveBlockConfig>
): ComposableBlock {
  let defaultPrompt = 'Notice the physical surge of urgency in your chest. Is this impulse aligned with your long-term plan, or an emotional reaction?';
  let title = 'CBT Thought Reframing';
  let instructions = 'Identify the automatic thought and formulate a grounded, balanced response.';
  let distortionTags = ['Catastrophizing', 'Emotional Reasoning'];

  if (variant === 'craving_vs_need') {
    defaultPrompt = 'Rate the current craving (1-10). What genuine unmet need (rest, connection, nourishment, comfort) is calling for attention?';
    title = 'Craving vs. Need Inquiry';
    instructions = 'Differentiate between an impulsive dopamine craving and a true bodily/emotional need.';
    distortionTags = ['Impulsivity', 'Dopamine Loop'];
  } else if (variant === 'loss_probability') {
    defaultPrompt = 'Consider the mathematical consequence: 83% of speculative momentum trades yield net losses. Would your future self thank you for stepping back?';
    title = 'Loss Probability & Consequence Perspective';
    instructions = 'Evaluate empirical outcomes and loss curves before committing resources.';
    distortionTags = ['Gambler Fallacy', 'Overconfidence'];
  } else if (variant === 'urge_surfing') {
    defaultPrompt = 'Visualize this urge as an ocean wave. It rises, peaks, and naturally subsides within 90 seconds without you acting on it.';
    title = 'Mindful Urge Surfing';
    instructions = 'Ride the crest of the emotional wave with detached, non-judgmental observation.';
    distortionTags = ['Urgency Illusion', 'All-or-Nothing'];
  }

  const cognitiveConfig: CognitiveBlockConfig = {
    variant,
    promptQuestion: prompt || defaultPrompt,
    distortionTags,
    minSentenceCount: 1,
    ...overrides,
  };

  return {
    id: generateBlockId('cog'),
    type: 'cognitive',
    title,
    instructions,
    cognitiveConfig,
  };
}

export function createDefaultFrictionBlock(
  variant: FrictionBlockConfig['variant'] = 'cooling_off_lock',
  durationSeconds: number = 60,
  overrides?: Partial<FrictionBlockConfig>
): ComposableBlock {
  let title = 'Cooling-Off Time-Lock';
  let instructions = 'Deliberate friction gives your executive prefrontal cortex time to re-engage.';
  let pledgeText = 'I commit to pausing before acting, choosing presence over immediate autopilot reaction.';
  let coreValues = ['Restorative Sleep', 'Family Time', 'Financial Freedom', 'Physical Vitality', 'Mental Clarity'];

  if (variant === 'values_contract') {
    title = 'Values-Alignment Contract';
    instructions = 'Reaffirm your core values and confirm your intentional commitment.';
  } else if (variant === 'reflection_prompt') {
    title = 'Intentional Reflection Pause';
    instructions = 'Write one sentence describing what you truly want out of this moment.';
  }

  const frictionConfig: FrictionBlockConfig = {
    variant,
    lockDurationSeconds: Math.max(5, durationSeconds),
    pledgeText,
    coreValues,
    ...overrides,
  };

  return {
    id: generateBlockId('frc'),
    type: 'friction',
    title,
    instructions,
    frictionConfig,
  };
}

export function createDefaultSupportBlock(
  variant: SupportBlockConfig['variant'] = 'voice_video_note',
  overrides?: Partial<SupportBlockConfig>
): ComposableBlock {
  let title = 'Therapist Supportive Voice Memo';
  let instructions = 'Listen to your clinician’s personalized supportive audio message.';
  let mediaTitle = 'Mindfulness Guide - 45s Urge Surfing Grounding';
  let mediaType: 'audio' | 'video' = 'audio';
  let transcript = 'Take a slow, deep breath right now. Feel your feet flat on the floor. Whatever urge you are experiencing is temporary, and you have the strength to let it pass safely.';
  let sponsorPhoneNumber = '';

  if (variant === 'crisis_988') {
    title = '988 Suicide & Crisis Lifeline';
    instructions = 'Immediate, confidential support is available 24/7. Tap to call or message a crisis counselor.';
    mediaTitle = 'National 988 Lifeline';
    sponsorPhoneNumber = '988';
    transcript = 'Call or text 988 anytime. Free, confidential support is available 24 hours a day, 7 days a week.';
  } else if (variant === 'sponsor_dialer') {
    title = 'Care Circle Sponsor Dialer';
    instructions = 'Reach out to your trusted partner, sponsor, or family care circle member.';
    mediaTitle = 'Care Circle Trusted Contact';
    sponsorPhoneNumber = '+1-800-555-0199';
    transcript = 'Contact your designated recovery support partner.';
  }

  const supportConfig: SupportBlockConfig = {
    variant,
    mediaUrl: 'https://cdn.luminousbreak.org/audio/sample_grounding_memo.mp3',
    mediaType,
    mediaTitle,
    transcript,
    sponsorPhoneNumber,
    ...overrides,
  };

  return {
    id: generateBlockId('sup'),
    type: 'support',
    title,
    instructions,
    supportConfig,
  };
}

// ---------------------------------------------------------------------------
// Default Canonical Presets for All 9 Domains (+ anxiety_news)
// ---------------------------------------------------------------------------

export const DEFAULT_DOMAIN_RECIPES: Record<BehavioralDomainId, InterventionRecipe> = {
  financial_speculation: {
    id: 'recipe_default_financial_speculation',
    name: 'Speculation Impulse Intercept',
    domainId: 'financial_speculation',
    isClinicianAssigned: true,
    clinicianName: 'Mindfulness Guide',
    updatedAt: '2026-09-13T22:00:00Z',
    blocks: [
      createDefaultSomaticBlock('diaphragmatic_mandala', { cycles: 3, inhaleSeconds: 5, exhaleSeconds: 5 }),
      createDefaultCognitiveBlock('loss_probability', 'Notice the urgency surge in your chest. Momentum trading carries an 83% net loss rate. Is this trade in your long-term plan?'),
      createDefaultFrictionBlock('cooling_off_lock', 120),
    ],
  },
  mindful_media: {
    id: 'recipe_default_mindful_media',
    name: 'Social Media Infinite Feed Pause',
    domainId: 'mindful_media',
    isClinicianAssigned: true,
    clinicianName: 'Mindfulness Guide',
    updatedAt: '2026-09-13T22:00:00Z',
    blocks: [
      createDefaultSomaticBlock('box_4_4_4', { cycles: 3 }),
      createDefaultCognitiveBlock('cbt_reframing', 'Are you opening this feed to find specific inspiration, or to escape an uncomfortable feeling right now?'),
      createDefaultFrictionBlock('values_contract', 30, { coreValues: ['Real-World Connection', 'Deep Work', 'Mental Peace'] }),
    ],
  },
  mindful_spending: {
    id: 'recipe_default_mindful_spending',
    name: 'Impulse Checkout Cooling-Off Protocol',
    domainId: 'mindful_spending',
    isClinicianAssigned: true,
    clinicianName: 'Mindfulness Guide',
    updatedAt: '2026-09-13T22:00:00Z',
    blocks: [
      createDefaultSomaticBlock('relaxing_4_7_8', { cycles: 3 }),
      createDefaultCognitiveBlock('craving_vs_need', 'This purchase equals hours of your focused work. Is this a genuine need or an impulsive dopamine reward?'),
      createDefaultFrictionBlock('cooling_off_lock', 60),
    ],
  },
  mindful_eating: {
    id: 'recipe_default_mindful_eating',
    name: 'Late-Night Food Delivery Reset',
    domainId: 'mindful_eating',
    isClinicianAssigned: true,
    clinicianName: 'Mindful Nutrition Specialist',
    updatedAt: '2026-09-13T22:00:00Z',
    blocks: [
      createDefaultSomaticBlock('diaphragmatic_mandala', { cycles: 3 }),
      createDefaultCognitiveBlock('cbt_reframing', 'Drink a glass of cold water and pause. Is your physical stomach hungry, or is your heart seeking comfort from stress?'),
    ],
  },
  substance_reduction: {
    id: 'recipe_default_substance_reduction',
    name: 'Urge Surfing & Supportive Voice Reset',
    domainId: 'substance_reduction',
    isClinicianAssigned: true,
    clinicianName: 'Mindfulness Guide',
    updatedAt: '2026-09-13T22:00:00Z',
    blocks: [
      createDefaultSomaticBlock('box_4_4_4', { cycles: 4 }),
      createDefaultCognitiveBlock('urge_surfing', 'Observe this physical craving as a wave that peaks and naturally recedes within minutes. You are safe in this moment.'),
      createDefaultSupportBlock('voice_video_note'),
      createDefaultSupportBlock('crisis_988'),
    ],
  },
  digital_dependency: {
    id: 'recipe_default_digital_dependency',
    name: 'Screen Time & Sleep Boundary Guardian',
    domainId: 'digital_dependency',
    isClinicianAssigned: true,
    clinicianName: 'Mindfulness Guide',
    updatedAt: '2026-09-13T22:00:00Z',
    blocks: [
      createDefaultSomaticBlock('relaxing_4_7_8', { cycles: 3 }),
      createDefaultFrictionBlock('cooling_off_lock', 60),
      createDefaultCognitiveBlock('cbt_reframing', 'It is late. Your brain needs restorative rest. Roll your shoulders, set your alarm, and put your phone down.'),
    ],
  },
  adult_media: {
    id: 'recipe_default_adult_media',
    name: 'Dopamine Loop Mindful Intercept',
    domainId: 'adult_media',
    isClinicianAssigned: true,
    clinicianName: 'Mindfulness Guide',
    updatedAt: '2026-09-13T22:00:00Z',
    blocks: [
      createDefaultSomaticBlock('relaxing_4_7_8', { cycles: 4 }),
      createDefaultCognitiveBlock('urge_surfing', 'Step back and observe the dopamine craving cycle without judgment. What emotional need (loneliness, boredom, stress) is seeking comfort?'),
      createDefaultFrictionBlock('values_contract', 120, { coreValues: ['Self-Respect', 'Integrity', 'Presence'] }),
    ],
  },
  gaming_microtransactions: {
    id: 'recipe_default_gaming_microtransactions',
    name: 'Gaming Loot & Microtransaction Cooldown',
    domainId: 'gaming_microtransactions',
    isClinicianAssigned: true,
    clinicianName: 'Mindfulness Guide',
    updatedAt: '2026-09-13T22:00:00Z',
    blocks: [
      createDefaultCognitiveBlock('craving_vs_need', 'This cosmetic item gives a momentary rush. Will this purchase matter to your life or goals 30 days from today?'),
      createDefaultFrictionBlock('cooling_off_lock', 120),
    ],
  },
  after_hours_work: {
    id: 'recipe_default_after_hours_work',
    name: 'Evening Work-Life Boundary Reaffirmation',
    domainId: 'after_hours_work',
    isClinicianAssigned: true,
    clinicianName: 'Mindfulness Guide',
    updatedAt: '2026-09-13T22:00:00Z',
    blocks: [
      createDefaultSomaticBlock('diaphragmatic_mandala', { cycles: 3 }),
      createDefaultCognitiveBlock('cbt_reframing', 'You are off the clock. Protect your evening recovery and peace. This notification can safely wait until tomorrow morning.'),
      createDefaultFrictionBlock('values_contract', 30, { coreValues: ['Restorative Sleep', 'Family Connection', 'Sustainable Career'] }),
    ],
  },
  anxiety_news: {
    id: 'recipe_default_anxiety_news',
    name: 'Panic Scrolling & Doomnews Intercept',
    domainId: 'anxiety_news',
    isClinicianAssigned: true,
    clinicianName: 'Mindfulness Guide',
    updatedAt: '2026-09-13T22:00:00Z',
    blocks: [
      createDefaultSomaticBlock('relaxing_4_7_8', { cycles: 3 }),
      createDefaultCognitiveBlock('cbt_reframing', 'Does reading this breaking headline change what is within your direct sphere of control right now? Step back and breathe.'),
      createDefaultSupportBlock('crisis_988'),
    ],
  },
};

export function createDefaultRecipeForDomain(
  domainId: BehavioralDomainId,
  clinicianId?: string,
  clinicianName?: string
): InterventionRecipe {
  const template = DEFAULT_DOMAIN_RECIPES[domainId] || DEFAULT_DOMAIN_RECIPES.financial_speculation;
  return {
    ...template,
    id: `recipe_${domainId}_${Date.now()}`,
    clinicianId: clinicianId || template.clinicianId,
    clinicianName: clinicianName || template.clinicianName,
    updatedAt: new Date().toISOString(),
    // Clone blocks so each recipe instance has independent block IDs
    blocks: template.blocks.map((b) => ({
      ...b,
      id: generateBlockId(b.type.substring(0, 3)),
    })),
  };
}

// ---------------------------------------------------------------------------
// Recipe Assembly & Reordering Logic
// ---------------------------------------------------------------------------

export function reorderBlocks(
  blocks: ComposableBlock[],
  fromIndex: number,
  toIndex: number
): ComposableBlock[] {
  if (fromIndex < 0 || fromIndex >= blocks.length) return [...blocks];
  if (toIndex < 0 || toIndex >= blocks.length) return [...blocks];
  if (fromIndex === toIndex) return [...blocks];

  const result = [...blocks];
  const [movedItem] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, movedItem);
  return result;
}

export function addBlockToRecipe(
  recipe: InterventionRecipe,
  block: ComposableBlock
): InterventionRecipe {
  return {
    ...recipe,
    blocks: [...recipe.blocks, block],
    updatedAt: new Date().toISOString(),
  };
}

export function removeBlockFromRecipe(
  recipe: InterventionRecipe,
  blockId: string
): InterventionRecipe {
  return {
    ...recipe,
    blocks: recipe.blocks.filter((b) => b.id !== blockId),
    updatedAt: new Date().toISOString(),
  };
}

export function updateBlockInRecipe(
  recipe: InterventionRecipe,
  blockId: string,
  updates: Partial<ComposableBlock>
): InterventionRecipe {
  return {
    ...recipe,
    blocks: recipe.blocks.map((b) => (b.id === blockId ? { ...b, ...updates } : b)),
    updatedAt: new Date().toISOString(),
  };
}

export function assignRecipeToClient(
  recipe: InterventionRecipe,
  clientId: string,
  clientName: string,
  clinicianId: string,
  clinicianName: string
): InterventionRecipe {
  const envelopeData = JSON.stringify({
    recipeId: recipe.id,
    domainId: recipe.domainId,
    clientId,
    clinicianId,
    blocksCount: recipe.blocks.length,
    assignedAt: new Date().toISOString(),
  });
  const simulatedCiphertext = typeof Buffer !== 'undefined'
    ? Buffer.from(envelopeData).toString('base64')
    : btoa(envelopeData);

  return {
    ...recipe,
    clientId,
    clientName,
    clinicianId,
    clinicianName,
    isClinicianAssigned: true,
    encryptedPayload: `zk_aes256gmac_${simulatedCiphertext}`,
    updatedAt: new Date().toISOString(),
  };
}

export function validateRecipe(recipe: InterventionRecipe): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!recipe.id || recipe.id.trim() === '') {
    errors.push('Recipe must have a non-empty ID');
  }
  if (!recipe.name || recipe.name.trim() === '') {
    errors.push('Recipe must have a title or name');
  }
  if (!recipe.domainId) {
    errors.push('Recipe must be associated with a valid behavioral domain');
  }
  if (!Array.isArray(recipe.blocks) || recipe.blocks.length === 0) {
    errors.push('Recipe must contain at least 1 composable intervention block');
  }

  // Validate each block
  const blockIds = new Set<string>();
  recipe.blocks?.forEach((block, idx) => {
    if (!block.id) {
      errors.push(`Block at index ${idx} missing ID`);
    } else if (blockIds.has(block.id)) {
      errors.push(`Duplicate block ID found: ${block.id}`);
    } else {
      blockIds.add(block.id);
    }

    if (!['somatic', 'cognitive', 'friction', 'support'].includes(block.type)) {
      errors.push(`Block "${block.title || idx}" has unknown type "${block.type}"`);
    }

    if (block.type === 'somatic') {
      if (!block.somaticConfig) {
        errors.push(`Somatic block "${block.title}" missing somaticConfig`);
      } else {
        if (block.somaticConfig.cycles < 1) errors.push('Somatic cycles must be >= 1');
        if (block.somaticConfig.inhaleSeconds < 1) errors.push('Inhale seconds must be >= 1');
        if (block.somaticConfig.exhaleSeconds < 1) errors.push('Exhale seconds must be >= 1');
      }
    }

    if (block.type === 'cognitive') {
      if (!block.cognitiveConfig) {
        errors.push(`Cognitive block "${block.title}" missing cognitiveConfig`);
      } else if (!block.cognitiveConfig.promptQuestion || block.cognitiveConfig.promptQuestion.trim() === '') {
        errors.push(`Cognitive block "${block.title}" requires a non-empty promptQuestion`);
      }
    }

    if (block.type === 'friction') {
      if (!block.frictionConfig) {
        errors.push(`Friction block "${block.title}" missing frictionConfig`);
      } else if (block.frictionConfig.lockDurationSeconds < 5) {
        errors.push(`Friction block "${block.title}" lock duration must be >= 5 seconds`);
      }
    }

    if (block.type === 'support') {
      if (!block.supportConfig) {
        errors.push(`Support block "${block.title}" missing supportConfig`);
      }
    }
  });

  return {
    valid: errors.length === 0,
    errors,
  };
}
